import { ChangeEvent, ReactElement, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import config from 'const/config'
import { FAR_INFO_CIRCLE } from 'const/icons'
import { formatDate } from 'syf-component-library/helpers/dateValidation'
import i18n from 'strings/i18n'
import { Stack } from 'syf-component-library/ui/spacing'
import { InputField } from './subcomponents'
import IncorrectInfoError from '../IncorrectInfoError'
import { FormState, ManageFormHelpers } from '../typings'
import { BodyContent, ContentWrapper, ImageWrapper } from '../subcomponents'

interface AddAccountScreenProps {
  formData: FormState
  formError: FormState
  setFormError?: ManageFormHelpers['setFormError']
  handleInputChange: ManageFormHelpers['handleInputChange']
  validateInput: ManageFormHelpers['validateInput']
  shouldShowIncorrectInfoError?: boolean
}

const AddAccountScreen = ({
  formData,
  formError,
  setFormError,
  handleInputChange,
  validateInput,
  shouldShowIncorrectInfoError
}: AddAccountScreenProps): ReactElement => {
  useEffect(() => {
    if (shouldShowIncorrectInfoError && setFormError) {
      setFormError({
        accountNumber: i18n({ errors: 'emptyAccountNumber' }) as string,
        ssn: i18n({ errors: 'emptySSN' }) as string,
        dob: i18n({ errors: 'emptyDOB' }) as string
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowIncorrectInfoError])

  const handleBlur = ({
    target: { name, value }
  }: ChangeEvent<HTMLInputElement>) => {
    validateInput(name, value)
  }

  const handleChange = ({
    target: { name, value }
  }: ChangeEvent<HTMLInputElement>) => {
    if (name === 'dob' && formData.dob.length <= value.length) {
      handleInputChange(name, formatDate(value))
    } else handleInputChange(name, value)
  }

  return (
    <ContentWrapper data-test="add-account-screen" data-cy="add-account-screen">
      <ImageWrapper>
        <img
          data-cy="add-account-screen-image"
          src={`${config.ASSETS_HOST}/assets/vista/add_account_modal.svg`}
          alt={i18n({ labels: 'addAccountContent' }) as string}
        />
      </ImageWrapper>
      <Stack size="large" />
      <BodyContent data-cy="add-account-content-title">
        {i18n({ labels: 'addAccountContent' })}
      </BodyContent>
      <Stack size="large" />
      {shouldShowIncorrectInfoError && <IncorrectInfoError />}
      <div>
        <InputField
          data-private
          data-test="add-account-number"
          dataCy="add-account-number"
          error={formError.accountNumber}
          errorIcon={<FontAwesomeIcon icon={FAR_INFO_CIRCLE} size="sm" />}
          helpText={i18n({ labels: 'addAccountHelpText' }) as string}
          id="add-account-number"
          matchPattern={/^\d{0,16}$/}
          name="accountNumber"
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder={i18n({ labels: 'accountNumberPlaceholder' }) as string}
          type="text"
          value={formData.accountNumber}
        />
        <Stack size="base" />
        <InputField
          data-private
          data-test="add-ssn"
          dataCy="add-ssn"
          error={formError.ssn}
          errorIcon={<FontAwesomeIcon icon={FAR_INFO_CIRCLE} size="sm" />}
          helpText={i18n({ labels: 'ssnHelpText' }) as string}
          id="add-ssn"
          matchPattern={/^\d{0,4}$/}
          name="ssn"
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder={i18n({ labels: 'ssnPlaceholder' }) as string}
          type="text"
          value={formData.ssn}
        />
        <Stack size="base" />
        <InputField
          data-private
          data-test="add-dob"
          dataCy="add-dob"
          error={formError.dob}
          errorIcon={<FontAwesomeIcon icon={FAR_INFO_CIRCLE} size="sm" />}
          helpText={i18n({ labels: 'dobHelpText' }) as string}
          id="add-dob"
          maxLength={10}
          name="dob"
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder={i18n({ labels: 'dobPlaceholder' }) as string}
          type="text"
          value={formData.dob}
        />
      </div>
      <Stack size="large" />
    </ContentWrapper>
  )
}

export default AddAccountScreen
